import WalletLabel from "@/client/components/common/WalletLabel";
import { CloseRoundCaps } from "@/client/components/icons/close-round-caps";
import { External } from "@/client/components/icons/external";
import { cn } from "@/client/lib/classnames";
import {
  getArtistCanonicalPath,
  getAssetPath,
  getCollectionPath,
  getWalletPath,
} from "@/client/lib/links";
import {
  CollectionGridTokenDataFragment,
  ProjectGridDataFragment,
} from "@/gql/graphql";
import { ChevronUpIcon } from "@heroicons/react/24/solid";
import Link from "next/link";
import slugify from "slugify";
import { FeatureValue } from ".";

type CollectionGridPageProps = {
  selectedProject: ProjectGridDataFragment;
  selectedToken: CollectionGridTokenDataFragment;
  onClose: () => void;
  highlightedFeatures: FeatureValue[];
  onFeatureValueClick: (featureValue: FeatureValue) => void;
  onToggleMobileTokenPopover: (expanded: boolean) => void;
  mobileTokenPopoverExpanded: boolean;
};

export function TokenDetail({
  selectedProject,
  selectedToken,
  onClose,
  highlightedFeatures,
  onFeatureValueClick,
  mobileTokenPopoverExpanded,
  onToggleMobileTokenPopover,
}: CollectionGridPageProps) {
  const assetPath =
    selectedProject && selectedToken
      ? getAssetPath(selectedProject.contract_address, selectedToken.token_id)
      : null;
  const collectionPath = selectedProject
    ? getCollectionPath(selectedProject.id)
    : null;
  const artistPath = selectedProject
    ? getArtistCanonicalPath({
        id: selectedProject.artist_address,
      })
    : null;
  const ownerPath = selectedToken?.owner_address
    ? getWalletPath(selectedToken.owner_address)
    : null;

  return (
    <>
      <div className="flex items-start">
        <div className="flex-1 p-4">
          <h2 className="mb-2 text-lg font-medium text-foreground">
            {selectedProject?.name} #{selectedToken?.invocation}
          </h2>
          {collectionPath ? (
            <Link
              href={collectionPath}
              className="text-sm text-foreground-muted"
            >
              {selectedProject?.name}
            </Link>
          ) : (
            <p className="text-sm text-foreground-muted">
              {selectedProject?.name}
            </p>
          )}
        </div>
        <div className="p-3">
          <button
            className="p-1.5 rounded-md bg-foreground/5 hover:bg-foreground/10 hidden md:block"
            onClick={onClose}
          >
            <CloseRoundCaps className="w-3 h-3" />
          </button>
          <button
            className="p-1.5 rounded-md bg-foreground/5 hover:bg-foreground/10 md:hidden transition-transform"
            onClick={() => {
              onToggleMobileTokenPopover(!mobileTokenPopoverExpanded);
            }}
          >
            <ChevronUpIcon
              className={cn("w-3 h-3", {
                "rotate-180": mobileTokenPopoverExpanded,
              })}
            />
          </button>
        </div>
      </div>

      <div className="flex justify-between p-4 border-b border-foreground/10 text-foreground">
        <p className="text-sm text-foreground-muted">Owner</p>
        <p className="text-sm">
          {ownerPath ? (
            <Link href={ownerPath} target="_blank">
              <WalletLabel address={selectedToken?.owner_address ?? ""} />
            </Link>
          ) : (
            <WalletLabel address={selectedToken?.owner_address ?? ""} />
          )}
        </p>
      </div>
      <div className="flex justify-between p-4 border-b border-foreground/10 text-foreground">
        <p className="text-sm text-foreground-muted">Artist</p>
        <p className="text-sm ">
          {artistPath ? (
            <Link href={artistPath} target="_blank">
              {selectedProject?.artist_name ?? ""}
            </Link>
          ) : (
            (selectedProject?.artist_name ?? "")
          )}
        </p>
      </div>
      {selectedToken?.features ? (
        <div className="border-b border-foreground/10">
          <div className="flex justify-between p-4">
            <p className="text-sm text-foreground-muted">Traits</p>
          </div>
          <div className="grid grid-cols-2 gap-2 p-4">
            {Object.entries(selectedToken.features).map(([key, value]) => {
              const featureFieldsCounts =
                selectedProject?.features?.feature_fields_counts;

              // Get the count for this specific feature value
              const count = featureFieldsCounts?.[key]?.[value as string] ?? 0;
              const totalTokens = selectedProject?.invocations ?? 0;
              const percentage = ((count / totalTokens) * 100).toFixed(1);

              return (
                <button
                  onClick={() => {
                    onFeatureValueClick({
                      feature: key,
                      value: value as string | number | boolean,
                    });
                  }}
                  className={cn(
                    "gap-2 p-3 border rounded-md border-foreground/10 cursor-pointer transition-colors text-left hover:bg-foreground/5",
                    "focus-visible:border-foreground/50 focus-visible:border-2 focus-visible:outline-none",
                    {
                      "bg-foreground/5": highlightedFeatures.some(
                        (f) => f.feature === key && f.value === value
                      ),
                    }
                  )}
                  key={key}
                >
                  <span className="block mb-2 text-sm text-foreground">
                    <span className="text-foreground-muted">{key}: </span>
                    <span>
                      {(value as string | number | boolean).toString()}
                    </span>
                  </span>
                  <span className="block text-sm text-foreground">
                    {count} &#8226; {percentage}%
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      {assetPath ? (
        <Link
          href={assetPath}
          className="flex items-center justify-between p-4 text-sm text-foreground-muted"
          passHref
          target="_blank"
        >
          <span>View Asset Page</span>
          <External className="w-6 h-6 stroke-foreground-muted" size={24} />
        </Link>
      ) : null}
    </>
  );
}
